import { FC } from 'react';
import { Stack } from '@mui/material';
import {
  AutocompleteInput,
  Modal,
  NumberInput,
  SelectInput,
  SwitchInput,
} from 'melp-design/components';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { useTranslation } from 'react-i18next';
import { ModalProps } from 'store/modal';
import { usePredefinedToasts } from 'utils/Toast';
import { FrequencyUnit, frequencyUnits } from 'types/general';
import {
  useCreateRecognitionAllowanceRule,
  useUpdateRecognitionAllowanceRule,
} from 'store/recognition-allowances';
import { useEmployeeGroups } from 'store/employee-groups';
import { usePointsLabel } from 'store/recognitions';

interface Values {
  points: number;
  frequency: FrequencyUnit;
  employeeGroups: string[];
  assignAllowanceOnCreation: boolean;
}

const INITIAL_VALUES: Values = {
  points: NaN,
  frequency: 'monthly',
  employeeGroups: [],
  assignAllowanceOnCreation: true,
};

interface Props extends ModalProps {
  allowanceRuleId?: string;
  initialValues?: Values;
}

export const AllowanceRuleModal: FC<Props> = ({
  closeModal,
  allowanceRuleId,
  initialValues,
}) => {
  const { t } = useTranslation();
  const predefinedToasts = usePredefinedToasts();
  const pointsLabel = usePointsLabel();

  const { control, handleSubmit } = useForm<Values>({
    defaultValues: initialValues ?? INITIAL_VALUES,
  });

  const { data: employeesGroups, isLoading: isEmployeeGroupsLoading } =
    useEmployeeGroups();

  const { mutate: createRule, isLoading: isCreating } =
    useCreateRecognitionAllowanceRule();
  const { mutate: updateRule, isLoading: isUpdating } =
    useUpdateRecognitionAllowanceRule();

  const onSubmit: SubmitHandler<Values> = (values) => {
    if (allowanceRuleId) {
      updateRule(
        {
          allowanceRuleId,
          data: {
            points: values.points || 0,
            frequency: values.frequency,
            employeeGroups: values.employeeGroups,
          },
        },
        {
          onSuccess: () => {
            predefinedToasts.success.updated();
            closeModal();
          },
        },
      );
    } else {
      createRule(
        {
          ...values,
          points: values.points || 0,
        },
        {
          onSuccess: () => {
            predefinedToasts.success.added();
            closeModal();
          },
        },
      );
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Content
        title={
          allowanceRuleId
            ? t('recognition.updateAllowanceRule')
            : t('recognition.newAllowanceRule')
        }
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal({ action: 'CLOSE' }),
            label: t('common.cancel'),
          },
          {
            variant: 'primary',
            label: t('common.save'),
            type: 'submit',
            disabled: isCreating || isUpdating,
          },
        ]}
      >
        <Stack spacing={2}>
          <Controller
            name="employeeGroups"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => {
              return (
                <AutocompleteInput
                  label={t('recognition.allowanceRuleGroups')}
                  name={name}
                  value={value}
                  onChange={onChange}
                  options={
                    employeesGroups?.items.map((group) => ({
                      label: group.name,
                      value: group.id,
                    })) ?? []
                  }
                  disabled={isEmployeeGroupsLoading}
                  multiple
                  required
                  ref={ref}
                />
              );
            }}
          />

          <Stack gap={2} flexDirection="row">
            <Controller
              name="points"
              control={control}
              render={({ field: { name, value, onChange, ref } }) => {
                return (
                  <NumberInput
                    label={pointsLabel}
                    name={name}
                    value={value}
                    onChange={onChange}
                    type="integer"
                    key={value}
                    sx={{ width: '100%' }}
                    infoMessage={t(
                      'recognition.allowanceRulePointsAndFrequency',
                      {
                        pointsName: pointsLabel,
                      },
                    )}
                    ref={ref}
                  />
                );
              }}
            />

            <Controller
              name="frequency"
              control={control}
              render={({ field: { name, value, onChange, ref } }) => {
                return (
                  <SelectInput
                    name={name}
                    value={value}
                    onChange={onChange}
                    label={t('recognition.allowanceRulesListFrequency')}
                    options={frequencyUnits
                      .filter((period) =>
                        ['weekly', 'monthly', 'quarterly', 'yearly'].includes(
                          period,
                        ),
                      )
                      .map((period) => ({
                        label: t(
                          `recognition.allowanceRuleFrequency-${period}`,
                        ),
                        value: period,
                      }))}
                    ref={ref}
                  />
                );
              }}
            />
          </Stack>

          {allowanceRuleId ? null : (
            <Controller
              name="assignAllowanceOnCreation"
              control={control}
              render={({ field: { name, value, onChange, ref } }) => (
                <SwitchInput
                  label={t('recognition.allowanceRuleInstantAssign')}
                  name={name}
                  value={value}
                  onChange={onChange}
                  ref={ref}
                />
              )}
            />
          )}
        </Stack>
      </Modal.Content>
    </form>
  );
};
